// ─────────────────────────────────────────────────────────────
//  Payout-Statement-Core – EINZIGE QUELLE DER WAHRHEIT für die Frage
//  „Was bekommt ein Teammitglied noch ausgezahlt?"
//
//  Reines CommonJS ohne Abhängigkeiten (außer billingCore), damit es BEIDE
//  Welten nutzen können:
//    • Frontend  -> Team Dashboard (Auszahlungen je Mitarbeiter)
//    • Cloud Fn  -> getMemberPayouts (teilbare Auszahlungsseite)
//  Die Kopie in functions/ wird beim Deploy über den predeploy-Hook in
//  firebase.json erzeugt – NUR DIESE DATEI BEARBEITEN.
//
//  ── Die drei Töpfe ────────────────────────────────────────────
//  FÄLLIG (open)          Der Kunde hat bezahlt, die Auszahlung steht als
//                         Datensatz in salesgod-research/payouts und wartet
//                         nur noch auf die Überweisung.
//  VORAUSSICHTLICH        Die Kundenrechnung ist gestellt/offen, aber noch
//  (expected)             nicht bezahlt. Die Auszahlung existiert deshalb noch
//                         nicht – sie wird hier mit exakt derselben Logik
//                         vorgerechnet, die sie später erzeugt
//                         (billingCore.payoutForPaidInvoice). Kein zweiter
//                         Rechenweg, keine Abweichung.
//  AUSGEZAHLT (paid)      Historie.
//
//  Beim Provisionsmodell hängt der Betrag an den Terminen des Monats. Sind sie
//  noch nicht erfasst, wird die Zeile trotzdem gezeigt – mit dem Hinweis
//  `appointmentsPending`, statt eine 0 zu behaupten, die nichts bedeutet.
// ─────────────────────────────────────────────────────────────

const billing = require('./billingCore');

const num = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);
const sum = (rows) => rows.reduce((s, r) => s + num(r.amount), 0);

// Ist dieses Mitglied dem Kunden zugewiesen? Liefert den Zuweisungszeitpunkt
// (ms) oder null, wenn keine Zuweisung besteht.
function assignmentAt(customer, memberId) {
  const assigned = customer && customer.team;
  if (!assigned || assigned[memberId] == null) return null;
  return typeof assigned[memberId] === 'number' ? assigned[memberId] : 0;
}

// Wurden für diese Rechnung überhaupt schon Termine erfasst? Ohne Erfassung
// steht die Termin-Provision noch nicht fest (siehe appointmentsOnPayment).
const appointmentsRecorded = (payment) =>
  typeof payment.appointments === 'number' || typeof payment.extraAppointments === 'number';

// Erwartet dieses Mitglied bei diesem Kunden eine gestaffelte Termin-Provision?
const hasTierCommission = (member, customer, memberId) =>
  billing.commissionActive(customer && customer.contract) &&
  !!billing.commissionRoleForMember(member, billing.customerRoleOverride(customer, memberId));

// Ein persistierter Auszahlungs-Datensatz in Anzeigeform.
function recordedRow(id, po) {
  return {
    id,
    source: 'payout',
    status: po.status === 'paid' ? 'paid' : po.status === 'cancelled' ? 'cancelled' : 'open',
    amount: num(po.amount),
    type: po.type || 'recurring',
    payType: po.payType || null,
    customerSlug: po.customerSlug || null,
    companyName: po.companyName || null,
    periodKey: po.periodKey || null,
    label: po.label || null,
    note: po.note || null,
    date: num(po.date),
    paidAt: typeof po.paidAt === 'number' ? po.paidAt : null,
    cancelReason: po.cancelReason || null,
    appointments: typeof po.appointments === 'number' ? po.appointments : null,
    commissionRole: po.commissionRole || null,
    appointmentsPending: false,
  };
}

// Eine noch nicht freigeschaltete Auszahlung (Kunde hat die Rechnung noch nicht
// bezahlt) in derselben Anzeigeform.
function expectedRow(id, payment, customer, po, appointmentsPending) {
  return {
    id,
    source: 'expected',
    status: 'expected',
    amount: po ? num(po.amount) : 0,
    type: payment.type === 'onetime' ? 'onetime' : 'recurring',
    payType: po ? po.payType : null,
    customerSlug: payment.customerSlug || null,
    companyName: payment.companyName || (customer && customer.companyName) || payment.customerSlug || null,
    periodKey: payment.periodKey || null,
    label: payment.label || null,
    note: null,
    date: num(payment.dueDate),
    paidAt: null,
    cancelReason: null,
    appointments: po && typeof po.appointments === 'number' ? po.appointments : null,
    commissionRole: po ? po.commissionRole || null : null,
    // Rechnung ist raus (invoiced) vs. steht noch offen (due) – erklärt, wie
    // weit die Zahlung des Kunden ist.
    invoiceStatus: payment.status === 'invoiced' ? 'invoiced' : 'due',
    appointmentsPending: !!appointmentsPending,
  };
}

// Alle voraussichtlichen Auszahlungen eines Mitglieds: abgeleitet aus den
// Kundenrechnungen, die noch NICHT bezahlt sind. Gerechnet wird mit derselben
// Funktion, die die echte Auszahlung erzeugt – die Rechnung wird dafür
// hypothetisch als „jetzt bezahlt" angenommen.
function expectedPayouts(memberId, member, opts) {
  const customers = (opts && opts.customers) || {};
  const payments = (opts && opts.payments) || {};
  const payouts = (opts && opts.payouts) || {};
  const now = (opts && opts.now) || Date.now();
  const out = [];

  Object.keys(payments).forEach((pid) => {
    const pay = payments[pid];
    if (!pay || pay.status === 'paid' || pay.status === 'cancelled') return;
    const slug = pay.customerSlug;
    const customer = slug && customers[slug];
    if (!customer || !pay.periodKey) return;
    // Nur die deterministisch erzeugten Rechnungen lösen Auszahlungen aus –
    // manuelle Einzelposten haben keine Ableitung ans Team.
    if (pid !== billing.paymentId(slug, pay.periodKey)) return;

    // Der Kunde zahlt frühestens jetzt: das ist der Zeitpunkt, an dem die
    // Auszahlung freigeschaltet würde (wichtig für die Zuweisungsprüfung).
    const asPaid = Object.assign({}, pay, { status: 'paid', paidAt: Math.max(num(pay.dueDate), now) });

    if (pay.type === 'onetime') {
      const oneTime = billing.findOneTime(customer, pay.oneTimeId || billing.oneTimeIdFromPeriodKey(pay.periodKey));
      if (!oneTime) return;
      const po = billing.payoutForPaidOneTime(member, oneTime, asPaid, memberId);
      if (!po) return;
      const id = billing.payoutId(memberId, slug, po.periodKey);
      if (payouts[id]) return; // schon als echte Auszahlung erfasst
      out.push(expectedRow(id, pay, customer, po, false));
      return;
    }

    if (pay.type !== 'recurring' || !customer.contract) return;
    const assignedAt = assignmentAt(customer, memberId);
    if (assignedAt === null) return;

    const po = billing.payoutForPaidInvoice(
      member, customer.contract, asPaid, assignedAt, billing.customerRoleOverride(customer, memberId),
    );
    const pending = !appointmentsRecorded(pay) && hasTierCommission(member, customer, memberId);
    // Ohne Betrag UND ohne offene Termin-Erfassung gibt es nichts zu zeigen.
    if (!po && !pending) return;
    const id = billing.payoutId(memberId, slug, po ? po.periodKey : pay.periodKey);
    if (payouts[id]) return;
    out.push(expectedRow(id, pay, customer, po, pending));
  });

  return out;
}

// Komplettes Auszahlungs-Statement EINES Mitglieds – die Datengrundlage der
// teilbaren Auszahlungsseite und der Übersicht im Team Dashboard.
function memberPayoutStatement(memberId, member, opts) {
  const payouts = (opts && opts.payouts) || {};
  const now = (opts && opts.now) || Date.now();

  const recorded = Object.keys(payouts)
    .filter((id) => payouts[id] && payouts[id].memberId === memberId)
    .map((id) => recordedRow(id, payouts[id]));

  const byDateDesc = (a, b) => (b.date || 0) - (a.date || 0);
  const open = recorded.filter((r) => r.status === 'open').sort(byDateDesc);
  const paid = recorded.filter((r) => r.status === 'paid').sort((a, b) => (b.paidAt || b.date || 0) - (a.paidAt || a.date || 0));
  const cancelled = recorded.filter((r) => r.status === 'cancelled').sort(byDateDesc);
  const expected = expectedPayouts(memberId, member, opts).sort(byDateDesc);

  const openTotal = sum(open);
  const expectedTotal = sum(expected);
  const monthStart = new Date(new Date(now).getFullYear(), new Date(now).getMonth(), 1).getTime();

  return {
    memberId,
    name: (member && member.name) || memberId,
    memberNo: member && typeof member.memberNo === 'number' ? member.memberNo : null,
    role: (member && member.role) || '',
    active: !(member && member.active === false),
    open,
    expected,
    paid,
    cancelled,
    totals: {
      // Was noch kommt – die Zahl, um die es dem Mitarbeiter geht.
      outstanding: openTotal + expectedTotal,
      open: openTotal,
      expected: expectedTotal,
      paid: sum(paid),
      paidThisMonth: sum(paid.filter((r) => (r.paidAt || r.date || 0) >= monthStart)),
      appointmentsPending: expected.filter((r) => r.appointmentsPending).length,
    },
    generatedAt: now,
  };
}

// Kurzübersicht über ALLE Mitglieder: wer bekommt noch was? Sortiert nach dem
// offenen Gesamtbetrag, damit oben steht, wer am meisten zu bekommen hat.
function payoutSummaryByMember(opts) {
  const team = (opts && opts.team) || {};
  return Object.keys(team)
    .map((memberId) => {
      const st = memberPayoutStatement(memberId, team[memberId], opts);
      return {
        memberId: st.memberId,
        name: st.name,
        role: st.role,
        memberNo: st.memberNo,
        active: st.active,
        outstanding: st.totals.outstanding,
        open: st.totals.open,
        expected: st.totals.expected,
        paid: st.totals.paid,
        openCount: st.open.length,
        expectedCount: st.expected.length,
        appointmentsPending: st.totals.appointmentsPending,
      };
    })
    .sort((a, b) => b.outstanding - a.outstanding || a.name.localeCompare(b.name));
}

module.exports = {
  memberPayoutStatement,
  payoutSummaryByMember,
  expectedPayouts,
};
